const { fetchJSON, getDefaultRequestConfig, getElement, postAddCartLine } = window.BAO.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} BaoCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {BaoCartSectionOptions} BaoCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * @param {HTMLFormElement} form
   * @param {BaoCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    // Remove empty line item properties from the FormData (e.g. properties[Color] with no value)
    const keysToRemove = []

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && (value === '' || value === null || value === undefined)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((k) => formData.delete(k))

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    if (window.vennApps === true) {
      const items = []

      for (const [key, value] of formData) {
        const m = key.match(/^items\[(\d+)]\[(.+)]$/)
        if (!m) continue

        const [, idx, path] = m
        const keys = path.split('][') // handles nested: "details][color" → ["details","color"]
        let obj = (items[idx] ??= {})

        // eslint-disable-next-line no-return-assign
        keys.slice(0, -1).forEach(k => (obj = obj[k] ??= {}))
        obj[keys.at(-1)] = value
      }

      if (!items[0]) items[0] = {}
      items[0].id = formData.get('id')
      items[0].product_id = formData.get('product_id')
      items[0].quantity = formData.get('quantity')
      items[0].properties = {}
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propertyName = key.slice(11, -1) // remove 'properties[' and ']'
          items[0].properties[propertyName] = value
        }
      }

      const payload = items.filter(Boolean).map(item => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
        productId: `gid://shopify/Product/${item.product_id}`, // uncomment if needed
        quantity: parseInt(item.quantity, 10) || 1,
        attributes: {
          ...item.properties,
        },
      }))

      payload.forEach(item => postAddCartLine(item)) // send line items to venn
    } else {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
        form,
        formData,
      })

      try {
        const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

        window.BAO.dispatchEvent(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
          {
            form,
            formData,
            state,
          },
        )

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })

        return state
      } catch (err) {
        this.handleAddToCartError(err)

        const pushCartSections = window.BAO.sectionRenderer.pushCartSections

        if (!pushCartSections || !pushCartSections.length) return

        window.BAO.sectionRenderer
          .fetch(pushCartSections.map((s) => s.section))
          .then((htmlSections) => {
            const updatedSections = pushCartSections.map((section) => ({
              ...section,
              html: htmlSections[section.section],
            }))

            window.BAO.sectionRenderer.renderMultiple(updatedSections)

            const cartCountHTML = htmlSections['push-cart-count']

            if (cartCountHTML) {
              window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
                state: {
                  sections: {
                    'push-cart-count': cartCountHTML,
                  },
                },
              })
            }
          })
          .catch((err) => {
            console.error('Failed to fetch cart sections:', err)
          })
      }
    }
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {BaoCartSectionOptionsWithAttributes & BaoCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    if (window.vennApps === true) {
      const payload = items.filter(Boolean).map(item => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
        productId: `gid://shopify/Product/${item.product_id}`,
        quantity: parseInt(item.quantity, 10),
        attributes: {
          ...item.attributes,
          ...item.properties,
        },
      }))

      payload.forEach(item => postAddCartLine(item)) // send line items to venn
    } else {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
        items,
        attributes,
        form,
      })

      try {
        const state = await this.cartAdd(body)

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
          items,
          state,
          form,
        })

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })

        return state
      } catch (err) {
        this.handleAddToCartError(err)
      }
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { key },
    )
    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    const body = {
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.BAO.dispatchEvent(window.BAO.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * Update cart discount codes
   *
   * @param {string} discount Comma-separated discount codes
   * @param {BaoCartSectionOptions} options Optional values
   *
   * @returns {Promise<Object>}
   */
  async updateCartDiscount (
    discount,
    {
      sections = window.BAO.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_DISCOUNT,
      {
        discount,
      },
    )

    const body = { discount }

    if (sections.length) {
      body.sections = sections

      if (sectionsUrl) {
        body.sections_url = sectionsUrl
      }
    }

    try {
      const state = await this.cartUpdate(body)

      if (state.sections) {
        const pushCartSections = window.BAO.sectionRenderer.pushCartSections
        if (pushCartSections && pushCartSections.length) {
          const updatedSections = pushCartSections.map((section) => ({
            ...section,
            html: state.sections[section.section],
          })).filter(section => section.html)

          if (updatedSections.length > 0) {
            window.BAO.sectionRenderer.renderMultiple(updatedSections)
          }
        }
      }

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_DISCOUNT,
        { state },
      )

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      this.checkValidCode(state)

      return state
    } catch (e) {
      console.error(e)
      const discountCodeForm = getElement('discount-code')
      if (discountCodeForm) {
        discountCodeForm.handleResponse({ applicable: false })
      }
    }
  }

  /**
   * Check if discount code is valid and notify discount code form
   *
   * @param {Object} state Cart state
   *
   * @returns {void}
   */
  checkValidCode (state) {
    const cart = state.cart || state

    const discountCodes = cart.discount_codes || []

    const discountApplications = cart.cart_level_discount_applications ||
      cart.discount_applications ||
      []

    const hasDiscount = discountCodes.length > 0 || discountApplications.length > 0
    const lastCode = discountApplications[0] || discountCodes[0]

    const discountCodeForm = getElement('discount-code')
    if (!discountCodeForm) return

    if (hasDiscount && lastCode) {
      discountCodeForm.handleResponse({ applicable: true, ...lastCode })
    } else {
      discountCodeForm.handleResponse({ applicable: false })
    }
  }
}

window.BAO.Cart = new BaoCart()
